import React, { useMemo, useRef, useState } from "react";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Typography,
} from "@mui/material";
import { Stack } from "@mui/material";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { HiOutlinePlusCircle } from "react-icons/hi";
import { MdOutlineKeyboardArrowDown } from "react-icons/md";

import { Document } from "../../models/document";
import { Job } from "../../models/job";
import { WaybillDocument } from "../../models/waybill.document";
import { Button } from "../Form";
import AutoSuggest from "../Form/AutoSuggest";
import { Color } from "../Form/Button";
import ActionButtons, { ActionButtonProps } from "../Generic/ActionButtons";
import ProgressBar from "../Generic/ProgressBar";
import SkeletonComponent from "../Generic/Skeleton";
import DocumentsItem from "./DocumentsItem";
import useStyles from "./styles";
import UploadDocumentModal, { UploadDocumentHandler } from "./UploadDocumentModal";

const EMPTY_DOC_IMAGE = require("../../assets/img/empty-documents.png");

interface DocumentsProps {
  isUploading?: boolean;
  documentsList: WaybillDocument[];
  requiredDocuments: Document[];
  isDisabled: boolean;
  withAccordion?: boolean;
  dataToRenderAccordions?: Job[];
  actionButtonProps?: ActionButtonProps;
  onUpload: (uri: string, document: WaybillDocument, jobId?: number) => void;
  onUploadViaModal: (
    uri: string,
    name: string,
    dueDate: Date,
    callbackFunc: () => void,
    jobId?: number
  ) => void;
}

const DocumentsComponent = (props: DocumentsProps) => {
  const {
    isUploading,
    documentsList,
    requiredDocuments,
    isDisabled,
    withAccordion = false,
    dataToRenderAccordions,
    actionButtonProps,
    onUpload,
    onUploadViaModal,
  } = props;

  const { t: translate } = useTranslation();
  const classes = useStyles();
  const modalRef = useRef<UploadDocumentHandler>(null);

  const [dataSelected, setDataSelected] = useState<Job>();
  const [documentLoading, setDocumentLoading] = useState<number>();

  const progress = useMemo(() => {
    let count = 0;
    let progress = 0;

    const divisor = requiredDocuments.length;

    documentsList.forEach((groupedDocument) => {
      const find = requiredDocuments.find(
        (requiredDocument) =>
          requiredDocument?.id === groupedDocument?.documentId && groupedDocument?.uploaded
      );

      if (find) {
        count += 1;
      }
    });

    if (divisor === 0) {
      progress = 100;
    } else if (count > 0 && count <= divisor) {
      progress = (count * 100) / divisor;
    }

    return { progress, count };
  }, [documentsList]);

  const renderWithAccordion = () => {
    if (dataToRenderAccordions && dataToRenderAccordions.length) {
      return (
        <div className={classes.wrapper}>
          {dataToRenderAccordions?.map((data, index) => (
            <Accordion key={index} className={classes.wrapperAccordion}>
              <AccordionSummary
                expandIcon={<MdOutlineKeyboardArrowDown data-cy="expand-accordion-button" />}
                className={classes.wrapperAccordionSummary}
              >
                <Typography
                  className={classes.accordionTitle}
                >{`Order #${data.reference}`}</Typography>
                <ProgressBar
                  value={progress.progress}
                  title={`${progress.count}/${requiredDocuments.length}`}
                />
              </AccordionSummary>
              <AccordionDetails>
                <Grid container direction="row" spacing={2} className={classes.documentsField}>
                  {documentsList
                    .filter((document) => document.jobId === data.id || !document.jobId)
                    .map((doc, index) => (
                      <DocumentsItem
                        key={index}
                        shipmentDocument={doc}
                        onUploadDocument={(uri: string) => onUpload(uri, doc, data.id)}
                        disabledUploadButton={isDisabled}
                        loadingUpload={documentLoading === index && isUploading}
                        setButtonClicked={() => setDocumentLoading(index)}
                      />
                    ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </div>
      );
    }

    return (
      <div data-cy="empty-component" className={classes.wrapperEmptyComponent}>
        <img src={EMPTY_DOC_IMAGE} className={classes.emptyDocumentImage} />
        <span>{translate("itFeelsABitEmpty")}</span>
      </div>
    );
  };

  const renderOnlyDocumentsList = () => (
    <Grid container direction="row" spacing={2} className={classes.documentsField}>
      {documentsList.map((doc, index) => (
        <DocumentsItem
          key={index}
          shipmentDocument={doc}
          onUploadDocument={(uri: string) => onUpload(uri, doc)}
          disabledUploadButton={isDisabled}
          loadingUpload={isUploading}
        />
      ))}
    </Grid>
  );

  const renderComponent = () => {
    if (withAccordion) return <div>{renderWithAccordion()}</div>;

    return renderOnlyDocumentsList();
  };

  return (
    <SkeletonComponent isLoading={false}>
      <div data-cy="documents-box" className={classes.mainDiv}>
        <div className={withAccordion ? classes.wrapperHeader : ""}>
          <span data-cy="documents-component" className={classes.title}>
            {translate("documents")}
          </span>

          <Stack
            direction="row"
            spacing={2}
            className={!withAccordion ? classes.documentsField : ""}
          >
            {!withAccordion && <ProgressBar value={progress.progress} />}
            <Button
              data-cy="create-new-document-button"
              customColor={Color.PRIMARY}
              className={classes.wrapperButton}
              onClick={() => modalRef.current?.onOpenModal()}
              disabled={
                withAccordion ? !dataToRenderAccordions || !dataToRenderAccordions?.length : false
              }
            >
              <HiOutlinePlusCircle size={16} className={classes.mr5} />
              <span>{translate("createNewDocument")}</span>
            </Button>
          </Stack>
        </div>
        <div className="mt-4">{renderComponent()}</div>

        <UploadDocumentModal
          ref={modalRef}
          isLoading={false}
          onUploadDocument={(url: string, name: string, dueDate: Date) => {
            if (dataSelected)
              onUploadViaModal(
                url,
                name,
                dueDate,
                () => modalRef?.current?.onCloseModal(),
                dataSelected?.id
              );
          }}
          isExtraFieldFill={!!dataSelected}
          renderAutosuggestInput={
            <AutoSuggest
              dataCy="job-autosuggest-field"
              label={translate("orderAssociated")}
              placeholder={translate("selectOrder")}
              options={dataToRenderAccordions ?? []}
              getOptionLabel={(option: Job) => `#${option.reference ?? ""}`}
              getOptionSelected={(option: Job, value: Job) => option.id === value.id}
              inputValue={`${dataSelected?.reference ?? ""}`}
              value={dataSelected}
              onChange={(value: Job) => setDataSelected(value)}
              required
            />
          }
          loadingUpload={isUploading}
        />

        <Box
          className={classNames(
            classes.actionButtons,
            actionButtonProps?.onInactive
              ? classes.withInactiveButton
              : classes.withoutInactiveButton
          )}
        >
          <ActionButtons {...actionButtonProps} />
        </Box>
      </div>
    </SkeletonComponent>
  );
};

export default DocumentsComponent;
